import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';
import { PartnerCard, type Summary } from './partners';

export interface SupplierDetails { name: string; companyCode: string; vatCode: string; address: string; email: string; iban: string; bankName: string }
export type Supplier = Summary & { details: SupplierDetails; year: { invoices: number; bought: number; paid: number } };

export const useSuppliers = (year?: number) => useQuery({ queryKey: ['suppliers', year ?? 'now'],
  queryFn: () => api.get<{ year: number; suppliers: Supplier[] }>(`api/v1/suppliers${year ? `?year=${year}` : ''}`) });

/** Tiekėjai: the companies we buy from, what we bought this year, what is unpaid; a click opens the partner card. */
export function SuppliersPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const data = useSuppliers(year);
  const needle = q.trim().toLowerCase();
  const rows = (data.data?.suppliers ?? []).filter((s) => !needle || s.name.toLowerCase().includes(needle) || s.details.companyCode.includes(needle) || s.details.vatCode.toLowerCase().includes(needle));
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Partneriai')} title={t('Tiekėjai')} text={t('Įmonės, iš kurių perkame: gautos sąskaitos, mokėjimai ir kas dar neapmokėta.')} />}>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      <section className="panel-card">
        <div className="recon-head">
          <label><span>{t('Paieška')}</span><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Pavadinimas arba kodas')} /></label>
          <label><span>{t('Metai')}</span><select value={year} onChange={(e) => setYear(Number(e.target.value))}>{[0, 1, 2].map((d) => new Date().getFullYear() - d).map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        </div>
        <div className="table-wrap"><table className="invoice-table">
          <thead><tr><th>{t('Tiekėjas')}</th><th>{t('Kodai')}</th><th className="num">{t('Sąskaitų')}</th><th className="num">{t('Pirkta {year} m.', { year })}</th><th className="num">{t('Neapmokėta')}</th><th>{t('Paskutinė')}</th></tr></thead>
          <tbody>
            {data.isSuccess && !rows.length ? <tr><td colSpan={6} className="empty">{t('Tiekėjų dar nėra — jie atsiranda iš gautų sąskaitų ir mokėjimų.')}</td></tr> : null}
            {rows.map((s) => (<Fragment key={s.key}>
              <tr className="clickable" onClick={() => setOpen(open === s.key ? null : s.key)}>
                <td><strong>{s.name}</strong>{s.details.address ? <small>{s.details.address}</small> : null}</td>
                <td>{s.details.companyCode || '—'}{s.details.vatCode ? <small>{s.details.vatCode}</small> : null}{s.details.iban ? <small>{s.details.iban}</small> : null}</td>
                <td className="num">{s.year.invoices}</td>
                <td className="num">{money(s.year.bought)}</td>
                <td className={`num${s.missing.unpaidAmount ? ' recon-late' : ''}`}>{s.missing.unpaidAmount ? money(s.missing.unpaidAmount) : '—'}</td>
                <td>{s.lastDate}</td>
              </tr>
              {open === s.key ? <tr className="partner-card-row"><td colSpan={6}><PartnerCard partnerKey={s.key} us={me.company.name} from={`${year}-01-01`} to={`${year}-12-31`} onClose={() => setOpen(null)}
                all={data.data?.suppliers ?? []} onAct={() => void navigate({ to: '/reconciliation', search: { tab: 'acts' } })} /></td></tr> : null}
            </Fragment>))}
          </tbody>
        </table></div>
      </section>
    </PanelShell>
  );
}
